import { Outlet, useLocation } from 'react-router'
import { useEffect, useRef } from 'react'
import { AppHeader, BottomNav, IndexRibbon } from '../ui'
import { useSession } from '../lib/session'
import { useConfig } from '../lib/config'
import { usePolled } from '../lib/usePolled'
import { LiveMarketConnection, useLiveIndices } from '../lib/liveMarket'
import type { MarketStatus, RibbonItem } from '../ui'
import styles from './AppShell.module.css'

function environmentLabel(): string | undefined {
  const h = window.location.hostname
  if (h.startsWith('new.')) return 'Staging'
  if (h === 'localhost' || h === '127.0.0.1') return 'Dev'
  return undefined
}

interface IndicesResponse {
  connected: boolean
  status?: MarketStatus
  data: Array<{ name: string; group: 'index' | 'mcx'; ltp: number; change: number | null; change_pct: number | null }>
}

export const DISCLAIMER = 'Information on corporate filings only. Not a recommendation to buy or sell.'

export function AppShell() {
  const { me } = useSession()
  const cfg = useConfig()
  // Session status + first paint from REST (60 s); values then tick live from the market stream.
  const indices = usePolled<IndicesResponse>(cfg.prices_enabled && me ? '/indices' : null, 60000)
  const live = useLiveIndices()
  const liveRows = [...live.indices.map(r => ({ ...r, group: 'index' as const })), ...live.mcx.map(r => ({ ...r, group: 'mcx' as const }))]
  const rows = liveRows.length ? liveRows : indices?.connected ? indices.data : []
  const ribbonItems: RibbonItem[] | null | undefined = indices === undefined && !liveRows.length ? undefined
    : !rows.length ? null
    : rows.map(d => ({ name: d.group === 'mcx' ? `${d.name} (MCX)` : d.name, value: d.ltp, change: d.change ?? 0, changePercent: d.change_pct ?? 0 }))
  const location = useLocation()
  const main = useRef<HTMLElement>(null)
  const first = useRef(true)

  // Move focus to the page on client-side navigation (not on first load).
  useEffect(() => {
    if (first.current) { first.current = false; return }
    main.current?.focus({ preventScroll: true })
    window.scrollTo(0, 0)
  }, [location.pathname])

  const user = me === undefined ? undefined : me ? { email: me.user.email, displayName: me.user.display_name, isAdmin: me.user.role === 'admin' } : null
  return (
    <div className={styles.shell}>
      <a href="#main" className="skip-link">Skip to content</a>
      <LiveMarketConnection />
      <AppHeader user={user} environment={environmentLabel()} />
      {/* Live index & MCX strip (Dhan snapshot, 15 s); hidden while prices are off or signed out. */}
      {cfg.prices_enabled && me && <IndexRibbon items={ribbonItems} status={indices?.status ?? null} />}
      <main id="main" ref={main} tabIndex={-1} className={styles.main}>
        <Outlet />
      </main>
      <footer className={styles.footer}>{DISCLAIMER}</footer>
      <BottomNav />
    </div>
  )
}
